import { v4 as uuidv4 } from 'uuid';

import { handleErrors } from '../../actions/ErrorActions';
import { addSuccess } from '../../actions/SuccessActions';
import {
  deleteReportModelById,
  exportReportModelApi,
  getDataModel,
  getFieldListByEntityName,
  getReportModelByName,
  importReportModelApi,
  saveReportModelByKey,
  verifyReportModelExists,
} from '../../api/report-model-designer';
import { downloadFile } from '../../utils/domUtils';
import {
  ADD_ENTITY_ACTION,
  DEFAULT_ACTION,
  DELETE_ENTITY_ACTION,
  EDIT_ENTITY_ACTION,
} from '../constants/report-model-designer.constants';
import { createSavePayload, findTreeItemById } from '../utils/report-model-designer-utils';

export const SET_REPORT_MODEL = 'SET_REPORT_MODEL';
export const SET_DATA_MODEL = 'SET_DATA_MODEL';
export const ADD_FIELD_LIST = 'ADD_FIELD_LIST';
export const CLEAR_REPORT_MODEL_DESIGNER = 'CLEAR_REPORT_MODEL_DESIGNER';

export const ADD_VIEW = 'ADD_VIEW';
export const EDIT_VIEW = 'EDIT_VIEW';
export const DELETE_VIEW = 'DELETE_VIEW';

export const SET_VIEWS = 'SET_VIEWS';

export const SET_SELECTED_VIEW_ID = 'SET_SELECTED_VIEW_ID';
export const SET_IS_DESIGN_PANEL_OPENED = 'SET_IS_DESIGN_PANEL_OPENED';

export const SET_DESIGN_PANEL_WIDTH = 'SET_DESIGN_PANEL_WIDTH';

export const SET_IS_NEW_REPORT_MODEL = 'SET_IS_NEW_REPORT_MODEL';
export const SET_IS_IMPORTED_REPORT_MODEL = 'SET_IS_IMPORTED_REPORT_MODEL';
export const SET_IS_REPORT_MODEL_EXISTS = 'SET_IS_REPORT_MODEL_EXISTS';

export const UPDATE_REPORT_MODEL_USER_ACTION_ARRAY = 'UPDATE_REPORT_MODEL_USER_ACTION_ARRAY';
export const UNDO_REPORT_MODEL_USER_ACTION = 'UNDO_REPORT_MODEL_USER_ACTION';
export const REDO_REPORT_MODEL_USER_ACTION = 'REDO_REPORT_MODEL_USER_ACTION';

export const SET_REPORT_MODEL_USER_ACTION_ARRAY = 'SET_REPORT_MODEL_USER_ACTION_ARRAY';

export const SET_REPORT_MODEL_RELEASE_TYPE = 'SET_REPORT_MODEL_RELEASE_TYPE';

export const setReportModel = (data) => ({
  type: SET_REPORT_MODEL,
  payload: data,
});

export const setDataModel = (data) => ({
  type: SET_DATA_MODEL,
  payload: data,
});

export const addFieldList = (data) => ({
  type: ADD_FIELD_LIST,
  payload: data,
});

export const addView = (data) => ({
  type: ADD_VIEW,
  payload: data,
});

export const setViews = (data) => ({
  type: SET_VIEWS,
  payload: data,
});

export const setDesignPanelWidth = (data) => ({
  type: SET_DESIGN_PANEL_WIDTH,
  payload: data,
});

export const setSelectedViewId = (data) => ({
  type: SET_SELECTED_VIEW_ID,
  payload: data,
});

export const setIsDesignPanelOpened = (data) => ({
  type: SET_IS_DESIGN_PANEL_OPENED,
  payload: data,
});

export const clearReportModelDesigner = () => ({
  type: CLEAR_REPORT_MODEL_DESIGNER,
});

export const setIsNewReportModel = (data) => ({
  type: SET_IS_NEW_REPORT_MODEL,
  payload: data,
});

export const updateSelectedViewId = () => (dispatch, getState) => {
  const { reportModel, selectedViewId } = getState().reportModelDesigner;

  const views = reportModel.views;

  const isSelectedViewExists = views.find((v) => v.Id === selectedViewId);

  if (!isSelectedViewExists) {
    dispatch(setSelectedViewId(''));
  }
};
export const setReportModelReleaseType = (data) => ({
  type: SET_REPORT_MODEL_RELEASE_TYPE,
  payload: data,
});

const setIsImportedReportModel = (data) => ({
  type: SET_IS_IMPORTED_REPORT_MODEL,
  payload: data,
});

const setIsReportModelExists = (data) => ({
  type: SET_IS_REPORT_MODEL_EXISTS,
  payload: data,
});

export const createReportModel = (name) => (dispatch) => {
  dispatch(
    setReportModel({
      reportModel: name,
      info: {
        ContentType: 'Model',
        ReportKey: name,
        ReportId: uuidv4(),
        ReportModel: name,
        ReportModelName: name,
        ReportLastModified: new Date(),
      },
      originalViews: [],
      views: [],
    })
  );

  dispatch(setUserActionArray({ actions: [{ actionType: DEFAULT_ACTION, views: [] }] }));

  dispatch(setIsNewReportModel(true));
};

export const loadReportModel = (name, id) => async (dispatch) => {
  try {
    const response = await getReportModelByName(name, id);

    const { hasError, resource } = response;

    const views = resource.ReportDesign.ReportModel.Views;

    if (!hasError) {
      dispatch(
        setReportModel({
          reportModel: resource.ReportModel,
          info: resource.SsReportInfo,
          originalViews: views,
          views,
        })
      );

      for (const entity of views) {
        await dispatch(loadFieldListByEntityName(entity.Name));
      }

      dispatch(setUserActionArray({ actions: [{ actionType: DEFAULT_ACTION, views }] }));
    } else {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`loadReportModel() failed: ${error}`);
  }
};

export const importReportModel = ({ file, redirectToReportModel }) => async (dispatch) => {
  try {
    const response = await importReportModelApi(file);

    const {
      data: { hasError, resource },
    } = response;

    if (!hasError) {
      const views = resource.ReportDesign.ReportModel.Views;

      dispatch(
        setReportModel({
          reportModel: resource.ReportModel,
          info: resource.SsReportInfo,
          originalViews: [],
          views,
        })
      );

      for (const entity of views) {
        await dispatch(loadFieldListByEntityName(entity.Name));
      }

      const { resource: isReportExist } = await verifyReportModelExists(resource.ReportModel);

      dispatch(setIsReportModelExists(isReportExist));

      redirectToReportModel({
        reportName: resource.ReportModel,
        reportId: resource.SsReportInfo.ReportId,
      });

      dispatch(setIsImportedReportModel(true));

      dispatch(addSuccess({ message: 'Report Model Imported Successfully' }));

      dispatch(setUserActionArray({ actions: [{ actionType: DEFAULT_ACTION, views }] }));
    } else {
      dispatch(handleErrors(response.data.errors));

      throw new Error('importReportModel() failed');
    }
  } catch (error) {
    throw new Error(`importReportModel() failed: ${error}`);
  }
};

export const loadDataModel = () => async (dispatch) => {
  try {
    const response = await getDataModel();

    const { hasError, resource } = response;

    if (!hasError) {
      dispatch(setDataModel(resource.DataModel.Tree));
    } else {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`loadDataModel() failed: ${error}`);
  }
};

export const loadFieldListByEntityName = (entityName) => async (dispatch) => {
  try {
    const response = await getFieldListByEntityName(entityName);

    const { hasError, resource } = response;

    if (!hasError && !resource) {
      return;
    }

    if (!hasError) {
      const list = resource.XsComplexType.XsSequence.XsElements.map((e) => e.Name);

      dispatch(
        addFieldList({
          list,
          entityName,
        })
      );

      return list;
    } else {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`loadFieldsListsByEntityName() failed: ${error}`);
  }
};

export const saveReportModel = ({ isSaveAs, reportName, redirectToReportModel }) => async (
  dispatch,
  getState
) => {
  try {
    const {
      isNewReportModel,
      isImportedReportModel,
      reportModel: model,
    } = getState().reportModelDesigner;

    const { views, info, reportModel } = model;

    const saveObj = createSavePayload(info, views, isSaveAs, reportName);

    const response = await saveReportModelByKey(saveObj);

    const { hasError, resource } = response;

    if (!hasError) {
      dispatch(addSuccess({ message: 'Saved Successfully' }));

      dispatch(
        setReportModel({
          reportModel: resource.ReportModel,
          info: resource.SsReportInfo,
          originalViews: resource.ReportDesign.ReportModel.Views,
          views: resource.ReportDesign.ReportModel.Views,
        })
      );

      const isKeyChanged = resource.ReportModel !== reportModel;
      const isIdChanged = info.ReportId !== resource.SsReportInfo.ReportId;

      if (isKeyChanged || isNewReportModel || isIdChanged || isImportedReportModel) {
        redirectToReportModel({
          reportName: resource.ReportModel,
          reportId: resource.SsReportInfo.ReportId,
        });
      }

      dispatch(setIsNewReportModel(false));
      dispatch(setIsImportedReportModel(false));
    } else {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`saveReportModel() failed: ${error}`);
  }
};

export const deleteReportModel = ({ redirectToReportModelDashboard }) => async (
  dispatch,
  getState
) => {
  try {
    const { reportModel: model } = getState().reportModelDesigner;

    const { info } = model;

    const response = await deleteReportModelById({ reportkey: info.ReportKey });

    const { hasError } = response;

    if (!hasError) {
      dispatch(addSuccess({ message: 'Report Model Deleted Successfully' }));

      redirectToReportModelDashboard();
    } else {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`deleteReportModel() failed: ${error}`);
  }
};

export const exportReportModel = () => async (dispatch, getState) => {
  try {
    const { reportModel } = getState().reportModelDesigner;

    const { info } = reportModel;

    const response = await exportReportModelApi({ id: info.ReportId });

    const { hasError } = response;

    if (!hasError) {
      downloadFile(`${info.ReportModelName}.xml`, response, 3);

      dispatch(addSuccess({ message: 'Exported Successfully' }));
    } else {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`exportReportModel() failed: ${error}`);
  }
};

export const addNewView = (nodeId) => async (dispatch, getState) => {
  try {
    const { reportModel, dataModel, selectedViewId } = getState().reportModelDesigner;

    const views = reportModel.views;

    if (selectedViewId || views.length === 0) {
      const treeItem = findTreeItemById(dataModel.TreeBranch, nodeId);

      if (treeItem) {
        const entityList = await dispatch(loadFieldListByEntityName(treeItem.SsdEntity));

        const sortedEntityListFirstValue = entityList ? entityList.sort()[0] : '';

        const parentView = views.length > 0 ? views.find((v) => v.Id === selectedViewId) : null;

        dispatch(
          addView({
            Id: uuidv4(),
            Name: treeItem.SsdEntity,
            EntityAlias: treeItem.Label.replaceAll(' ', ''),
            Filter: null,
            ObjectKey: sortedEntityListFirstValue,
            LinkSource: sortedEntityListFirstValue,
            LinkTarget: parentView ? parentView.ObjectKey : '',
            Alias: '',
            ParentId: parentView ? selectedViewId : '',
            TranslatedName: treeItem.Label,
            HasChildren: false,
          })
        );

        dispatch(updateUserActionArray({ actionType: ADD_ENTITY_ACTION }));
      }
    }
  } catch (error) {
    throw new Error(`addNewView() failed: ${error}`);
  }
};

export const editView = (data) => (dispatch) => {
  dispatch({ type: EDIT_VIEW, payload: data });

  dispatch(updateUserActionArray({ actionType: EDIT_ENTITY_ACTION }));
};

export const deleteView = (data) => (dispatch) => {
  dispatch({ type: DELETE_VIEW, payload: data });

  dispatch(updateSelectedViewId());

  dispatch(updateUserActionArray({ actionType: DELETE_ENTITY_ACTION }));
};

export const setUserActionArray = (data) => ({
  type: SET_REPORT_MODEL_USER_ACTION_ARRAY,
  payload: data,
});

export const updateUserActionArray = (data) => ({
  type: UPDATE_REPORT_MODEL_USER_ACTION_ARRAY,
  payload: data,
});

export const undoUserAction = () => (dispatch, getState) => {
  const { userActions } = getState().reportModelDesigner;

  const { actions, actionPointer } = userActions;

  const views = actions[actionPointer + 1].views;

  dispatch(setViews(views));

  dispatch(updateSelectedViewId());

  dispatch({ type: UNDO_REPORT_MODEL_USER_ACTION });
};

export const redoUserAction = () => (dispatch, getState) => {
  const { userActions } = getState().reportModelDesigner;

  const { actions, actionPointer } = userActions;

  const views = actions[actionPointer - 1].views;

  dispatch(setViews(views));

  dispatch(updateSelectedViewId());

  dispatch({ type: REDO_REPORT_MODEL_USER_ACTION });
};
